import { labelStyle, inputStyle, fieldStyle } from '../../constants/general';
import clsx from 'clsx';

export const FormFields = ({ formik }) => {
  // Define locked field IDs
  const lockedFieldIds = ['userEmail', 'asanaTaskName'];

  const addField = () => {
    const newFields = [
      ...formik.values.formFields,
      { id: '', label: '', type: 'text', required: false },
    ];
    formik.setFieldValue('formFields', newFields);
  };

  const updateField = (index, key, value) => {
    const field = formik.values.formFields[index];

    // Prevent updates to locked fields (except id can still be changed)
    if (lockedFieldIds.includes(field.id) && key !== 'id') {
      return;
    }

    const newFields = formik.values.formFields.map((field, i) =>
      i === index ? { ...field, [key]: value } : field,
    );
    formik.setFieldValue('formFields', newFields);
  };

  const removeField = index => {
    const field = formik.values.formFields[index];

    // Prevent removal of locked fields
    if (lockedFieldIds.includes(field.id)) {
      return;
    }

    const newFields = formik.values.formFields.filter((_, i) => i !== index);
    formik.setFieldValue('formFields', newFields);
  };

  // Check if a field is locked
  const isFieldLocked = field => lockedFieldIds.includes(field.id);

  return (
    <div className="mb-6 w-full">
      <label className={labelStyle}>Form Fields</label>

      <p className="mt-4 p-3 italic text-sm border border-blue-200 bg-blue-50 rounded">
        <span className="font-semibold not-italic">Note: </span>This form requires two default
        fields to create Asana tasks (do not remove): userEmail and asanaTaskName
      </p>

      {formik.values.formFields.map((field, index) => {
        const locked = isFieldLocked(field);

        return (
          <div key={index}>
            <div className="flex justify-between items-center mt-6 mb-4">
              <p className="font-bold text-navy">
                Field {index + 1}:
                {locked && (
                  <span className="ml-2 text-xs font-normal text-blue-600 bg-blue-100 px-2 py-1 rounded">
                    Required Field
                  </span>
                )}
              </p>

              <button
                type="button"
                className={clsx(
                  'text-sm text-white font-medium py-2 px-4 rounded-lg transition-all',
                  locked ? 'bg-gray-300 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600',
                )}
                onClick={() => removeField(index)}
                disabled={locked}
              >
                Remove
              </button>
            </div>

            <div className="flex flex-col">
              <input
                type="text"
                className={clsx(
                  'mb-2 w-1/2',
                  inputStyle,
                  locked && 'bg-gray-100 cursor-not-allowed',
                )}
                placeholder="Field id"
                value={field.id}
                onChange={e => updateField(index, 'id', e.target.value)}
                disabled={locked}
              />
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    type="text"
                    className={clsx(inputStyle, locked && 'bg-gray-100 cursor-not-allowed')}
                    placeholder="Field label"
                    value={field.label}
                    onChange={e => updateField(index, 'label', e.target.value)}
                    disabled={locked}
                  />
                  {formik.errors.formFields?.[index]?.name && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.formFields[index].name}
                    </div>
                  )}
                </div>

                <div className={fieldStyle}>
                  <select
                    className={clsx(inputStyle, locked && 'bg-gray-100 cursor-not-allowed')}
                    value={field.type}
                    onChange={e => updateField(index, 'type', e.target.value)}
                    disabled={locked}
                  >
                    <option value="">--- Select ---</option>
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="number">Number</option>
                    <option value="dropdown">Dropdown</option>
                    <option value="textarea">Text Area</option>
                    <option value="date">Date</option>
                  </select>
                  {formik.errors.formFields?.[index]?.type && (
                    <div className="text-red-500 text-sm mt-1">
                      {formik.errors.formFields[index].type}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center mb-4">
                {/* Number */}
                {field.type === 'number' && (
                  <div className="flex gap-3 mt-4">
                    <input
                      type="number"
                      className={inputStyle}
                      placeholder="Min value"
                      value={field.min || ''}
                      onChange={e => updateField(index, 'min', e.target.value)}
                    />

                    <input
                      type="number"
                      className={inputStyle}
                      placeholder="Max value"
                      value={field.max || ''}
                      onChange={e => updateField(index, 'max', e.target.value)}
                    />
                  </div>
                )}

                {/* Dropdown */}
                {field.type === 'dropdown' && (
                  <div className="mt-4 space-y-2">
                    {field.options?.map((option, optIndex) => (
                      <div key={optIndex} className="flex gap-2">
                        <input
                          type="text"
                          className={inputStyle}
                          value={option}
                          onChange={e => {
                            const newFields = formik.values.formFields.map((f, i) =>
                              i === index
                                ? {
                                    ...f,
                                    options: f.options.map((opt, oi) =>
                                      oi === optIndex ? e.target.value : opt,
                                    ),
                                  }
                                : f,
                            );
                            formik.setFieldValue('formFields', newFields);
                          }}
                          placeholder={`Option ${optIndex + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newFields = formik.values.formFields.map((f, i) =>
                              i === index
                                ? {
                                    ...f,
                                    options: f.options.filter((_, oi) => oi !== optIndex),
                                  }
                                : f,
                            );
                            formik.setFieldValue('formFields', newFields);
                          }}
                          className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const newFields = formik.values.formFields.map((f, i) =>
                          i === index ? { ...f, options: [...(f.options || []), ''] } : f,
                        );
                        formik.setFieldValue('formFields', newFields);
                      }}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    >
                      + Add Option
                    </button>

                    {formik.errors.formFields?.[index]?.options && (
                      <div className="text-red-500 text-sm mt-1">
                        {formik.errors.formFields[index].options}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Required Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`required-${index}`}
                  checked={field.required ?? false}
                  onChange={e => updateField(index, 'required', e.target.checked)}
                  className={clsx('w-4 h-4', locked && 'cursor-not-allowed')}
                  disabled={locked}
                />

                <label htmlFor={`required-${index}`} className="text-sm font-medium">
                  Required
                </label>
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="px-4 py-2 mt-8 bg-blue-500 text-white rounded hover:bg-blue-600"
        onClick={addField}
      >
        + Add Field
      </button>
    </div>
  );
};
